import { DiscordSDK } from '@discord/embedded-app-sdk';
import type {
  DiscordParticipant,
  DiscordSdkPort,
  SpeakingEvent,
} from './DiscordSdkPort.js';

/**
 * Adapter from `@discord/embedded-app-sdk` to our `DiscordSdkPort`.
 *
 * Two files are allowed to import `@discord/embedded-app-sdk` directly:
 * - `realDiscordSdkAdapter.ts` (this file): wraps the SDK behind `DiscordSdkPort`.
 * - `discordProxyMappings.ts`: calls `patchUrlMappings` (a one-time global
 *   monkey-patch; not part of the SDK communication interface).
 * All other client code MUST go through `DiscordSdkPort` so unit tests can
 * inject a fake without touching the real SDK (which only runs inside a
 * Discord Activity iframe).
 *
 * VERIFIED on 2026-05-08 against @discord/embedded-app-sdk@2.5.0
 * (output/commands/authorize.d.ts, authenticate.d.ts, index.d.ts):
 * - `new DiscordSDK(clientId)` synchronously throws plain `Error` if URL
 *   query params (`frame_id`, `instance_id`, `platform`) are missing —
 *   i.e., when not running inside a Discord Activity iframe. The
 *   BootErrorBoundary around RealApp catches that and shows the
 *   outside-Discord closed-door screen; there is no guest fallback.
 * - `sdk.ready()` resolves on the RPC `READY` event from the host.
 * - `sdk.commands.authorize({ client_id, scope: OAuthScopes[],
 *   response_type?: 'code', state?: string, prompt?: 'none' })` returns
 *   `Promise<{ code: string }>`. `'identify'` is a valid `OAuthScopes`
 *   literal. `authorize` is the call that triggers Discord's consent
 *   modal on first launch.
 * - `sdk.commands.authenticate({ access_token?: string | null | undefined })` returns
 *   `Promise<{ access_token, user: { id, username, discriminator,
 *   public_flags, avatar?, global_name? }, scopes, expires, application }>`.
 *   The `auth.user` shape narrows cleanly to `DiscordParticipant`'s five
 *   required fields (id, username, discriminator, avatar, global_name);
 *   `auth.user` lacks `bot` and uses `public_flags` instead of `flags`,
 *   but `DiscordParticipant` does NOT include `bot`/`flags` — narrowing
 *   is field-by-field on the intersection.
 * - `sdk.commands.getInstanceConnectedParticipants()` returns
 *   `{ participants: [...] }` with the per-record fields documented on
 *   `DiscordSdkPort` / `DiscordParticipant`. Required `discriminator` is
 *   non-nullable string (`'0'` for pomelo). The SDK record carries
 *   `bot`/`flags`/etc. that we don't use; the map at the bottom narrows
 *   to our subset.
 * - `sdk.commands.openExternalLink({ url: string })` returns
 *   `Promise<{ opened: boolean | null }>` (verified 2026-06-10).
 */
/** The resolved shape of `sdk.commands.authenticate(...)` — memoized and
 *  shared by getCurrentUser and subscribeSpeaking via ensureAuthenticated. */
type AuthenticateResult = Awaited<ReturnType<DiscordSDK['commands']['authenticate']>>;

export class RealDiscordSdkAdapter implements DiscordSdkPort {
  private readonly sdk: DiscordSDK;
  private readonly clientId: string;
  /** Memoized authorize → token-exchange → authenticate. Shared so the RPC
   *  connection authenticates exactly once; a rejection clears it for retry. */
  private authentication: Promise<AuthenticateResult> | null = null;

  // The optional `sdk` is injected only by unit tests; the real SDK runs
  // solely inside a Discord iframe, so production always takes the default.
  constructor(clientId: string, sdk?: DiscordSDK) {
    this.clientId = clientId;
    this.sdk = sdk ?? new DiscordSDK(clientId);
  }

  get instanceId(): string {
    return this.sdk.instanceId;
  }

  async ready(): Promise<void> {
    await this.sdk.ready();
  }

  async getCurrentUser(): Promise<DiscordParticipant> {
    const auth = await this.ensureAuthenticated();
    return this.toDiscordParticipant(auth.user);
  }

  /**
   * Runs the Discord Activity OAuth flow once and memoizes it. Both
   * getCurrentUser (identity) and subscribeSpeaking (voice events) need the
   * RPC connection authenticated, so they share one promise: a single
   * authorize/authenticate round-trip, and subscribeSpeaking can await the
   * very auth the identity flow performs. A rejection clears the cache so a
   * transient failure stays retryable (mirrors DiscordIdentityProvider.ensureReady).
   */
  private ensureAuthenticated(): Promise<AuthenticateResult> {
    if (this.authentication === null) {
      this.authentication = this.runAuthentication().catch((err: unknown) => {
        this.authentication = null;
        throw err;
      });
    }
    return this.authentication;
  }

  private async runAuthentication(): Promise<AuthenticateResult> {
    // The Discord Activity OAuth flow:
    //   1. authorize() returns an OAuth code. On a user's first launch — or the
    //      first launch after a new scope is added — Discord shows the consent
    //      modal; once every requested scope is granted, prompt:'none' resolves
    //      it silently on later launches.
    //   2. server exchanges code → access_token via Discord's /oauth2/token
    //      endpoint (server-side only; client_secret can't live in the browser).
    //   3. authenticate(token) authorizes the RPC connection with the granted
    //      scopes and returns the user record. Scoped subscriptions (SPEAKING_*)
    //      are only permitted AFTER this resolves — see subscribeSpeaking.
    const { code } = await this.sdk.commands.authorize({
      client_id: this.clientId,
      response_type: 'code',
      state: '',
      prompt: 'none',
      // 'rpc.voice.read' gates the SPEAKING_START/STOP subscription. Granting
      // is all-or-nothing in this one call; adding the scope re-shows the
      // consent modal once for users who authorized before it existed.
      scope: ['identify', 'rpc.voice.read'],
    });
    const accessToken = await this.exchangeCodeForToken(code);
    return this.sdk.commands.authenticate({ access_token: accessToken });
  }

  async getInstanceConnectedParticipants(): Promise<DiscordParticipant[]> {
    const result = await this.sdk.commands.getInstanceConnectedParticipants();
    return result.participants.map((p) => ({
      id: p.id,
      username: p.username,
      global_name: p.global_name ?? null,
      avatar: p.avatar ?? null,
      discriminator: p.discriminator,
    }));
  }

  async openExternalLink(url: string): Promise<void> {
    await this.sdk.commands.openExternalLink({ url });
  }

  async subscribeSpeaking(
    listener: (event: SpeakingEvent) => void,
  ): Promise<() => Promise<void>> {
    // Nullable in the SDK type — an activity context without a voice channel
    // has no speaking events; hand back an inert subscription.
    const channelId = this.sdk.channelId;
    if (channelId === null) {
      return async (): Promise<void> => {};
    }
    // Discord rejects a SUBSCRIBE to scoped voice events (SPEAKING_*) on a
    // connection that hasn't completed authenticate(). ready() only resolves
    // the RPC handshake — which happens well before authenticate (a consent
    // step + server token exchange) — so subscribing after ready() alone races
    // ahead of auth and the SUBSCRIBE is rejected. Wait for the shared,
    // memoized authentication. (ready() stays first: authorize() inside the
    // auth flow needs the handshake.)
    await this.sdk.ready();
    await this.ensureAuthenticated();
    const onStart = (data: { user_id: string }): void => {
      listener({ userId: data.user_id, speaking: true });
    };
    const onStop = (data: { user_id: string }): void => {
      listener({ userId: data.user_id, speaking: false });
    };
    const args = { channel_id: channelId };
    try {
      await Promise.all([
        this.sdk.subscribe('SPEAKING_START', onStart, args),
        this.sdk.subscribe('SPEAKING_STOP', onStop, args),
      ]);
    } catch (err) {
      // Partial subscription would leak a live listener (and with only one of
      // START/STOP registered, a speaker could stick forever). Roll back
      // best-effort, then let the caller's failure policy handle the rest.
      await Promise.allSettled([
        this.sdk.unsubscribe('SPEAKING_START', onStart, args),
        this.sdk.unsubscribe('SPEAKING_STOP', onStop, args),
      ]);
      throw err;
    }
    return async (): Promise<void> => {
      // unsubscribe needs the identical (event, listener, args) triple.
      await Promise.all([
        this.sdk.unsubscribe('SPEAKING_START', onStart, args),
        this.sdk.unsubscribe('SPEAKING_STOP', onStop, args),
      ]);
    };
  }

  /**
   * Exchange the OAuth `code` for a Discord `access_token` via the
   * `/api/discord/token` Cloudflare Pages Function (Epic 13). The Function
   * holds `DISCORD_CLIENT_SECRET` in its environment; the client never sees it.
   */
  private async exchangeCodeForToken(code: string): Promise<string> {
    const res = await fetch('/api/discord/token', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ code }),
    });
    if (!res.ok) {
      throw new Error(`Discord token exchange failed: ${res.status}`);
    }
    const body = await res.json() as { access_token?: unknown };
    if (typeof body.access_token !== 'string') {
      throw new Error('Discord token exchange returned malformed body');
    }
    return body.access_token;
  }

  private toDiscordParticipant(user: {
    id: string;
    username: string;
    discriminator: string;
    avatar?: string | null | undefined;
    global_name?: string | null | undefined;
  }): DiscordParticipant {
    return {
      id: user.id,
      username: user.username,
      global_name: user.global_name ?? null,
      avatar: user.avatar ?? null,
      discriminator: user.discriminator,
    };
  }
}
